/**
 * MobileItemsSheet — T146
 *
 * Bottom-sheet wrapper that displays a transaction's line items in a
 * receipt-style read-only view, used on mobile in place of the inline
 * expansion under the transaction card.
 *
 * Pattern: shadcn/ui Sheet (side="bottom") + ReceiptItemRow + ItemizedSummaryBar.
 * @see https://ui.shadcn.com/docs/components/sheet
 */
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import ReceiptItemRow from './ReceiptItemRow';
import ItemizedSummaryBar from './ItemizedSummaryBar';
import { useTimezone } from '@/hooks/useTimezone';
import type { TransactionItem } from '@/lib/types';

interface MobileItemsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: TransactionItem[];
  transactionAmount: number;
  transactionDiscountTotal?: number;
  transactionDate: string | Date;
  transactionDescription: string;
  transactionEmoji?: string;
  /** ISO 4217 currency code (passed to children for parity). */
  currency?: string;
}

export default function MobileItemsSheet({
  open,
  onOpenChange,
  items,
  transactionAmount,
  transactionDiscountTotal = 0,
  transactionDate,
  transactionDescription,
  transactionEmoji,
  currency,
}: MobileItemsSheetProps) {
  const { formatInUserTimezone } = useTimezone();

  // T146: Compute itemized total inside the component so callers don't have to.
  const itemSubtotal = items.reduce(
    (sum, item) => sum + item.amount * (item.quantity ?? 1),
    0,
  );
  const itemDiscountTotal = items.reduce((sum, item) => sum + (item.discountAmount ?? 0), 0);
  const itemsTotal = itemSubtotal - itemDiscountTotal;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[80vh] flex flex-col p-0"
        data-testid="mobile-items-sheet"
      >
        <SheetHeader className="px-4 py-3 border-b">
          <SheetTitle className="flex items-center gap-2 text-left">
            <span aria-hidden="true">{transactionEmoji || '🧾'}</span>
            <span className="break-words">{transactionDescription}</span>
          </SheetTitle>
          <SheetDescription className="text-xs tabular-nums text-left">
            {formatInUserTimezone(transactionDate, 'medium')}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-2 divide-y divide-border/50">
          {items.map((item, i) => (
            <ReceiptItemRow
              key={i}
              item={item}
              currency={currency}
              rowNumber={i + 1}
            />
          ))}
        </div>

        {/* T146: Sticky summary footer — backdrop blur for receipt-strip feel */}
        <div className="border-t p-3 sticky bottom-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <ItemizedSummaryBar
            itemizedTotal={itemsTotal}
            transactionTotal={transactionAmount}
            itemSubtotal={itemSubtotal}
            itemDiscountTotal={itemDiscountTotal}
            transactionDiscountTotal={transactionDiscountTotal}
            currency={currency}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
